
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { CheckOutlined, ContentCopyOutlined } from '@mui/icons-material'
import { requestVoiceTranscription } from '../../actions/message'
import {
    downloadBlob,
    fetchChatAttachment,
    fetchChatDrawingPreview,
    getChatAttachmentMimeType,
    getChatAttachmentName,
    getChatAttachmentType,
    hasSecureChatAttachment,
} from '../../utils/chatAttachmentStore'
import { getFileExtensionLabel, getFileTypeLabel, isDrawingFile } from '../../utils/mediaDocument'
import '../../scss/components/_chat-file-card.scss'

const VOICE_RATES = [1, 1.5, 2]
let activeVoiceAudio = null

const getAccessErrorText = (error) => {
    const status = Number(error?.response?.status || 0)

    if (status === 401 || status === 403) return 'Нет доступа к этому вложению'
    if (status === 404) return 'Вложение удалено или недоступно'

    return 'Не удалось загрузить вложение'
}

const formatBytes = (value) => {
    const bytes = Number(value || 0)
    if (!bytes) return ''
    if (bytes < 1024) return `${bytes} Б`
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`
    return `${(bytes / 1024 / 1024).toFixed(bytes >= 10 * 1024 * 1024 ? 0 : 1)} МБ`
}

const formatDuration = (value) => {
    const seconds = Math.max(0, Math.floor(Number(value || 0)))
    const minutes = Math.floor(seconds / 60)
    return `${minutes}:${String(seconds % 60).padStart(2, '0')}`
}

const getVoiceMeta = (message) => (
    message?.voiceId && typeof message.voiceId === 'object'
        ? message.voiceId
        : null
)

const normalizeWaveform = (value) => (
    Array.isArray(value)
        ? value
            .map((item) => Math.max(0, Math.min(100, Number(item) || 0)))
            .filter((item) => Number.isFinite(item))
        : []
)

const compressWaveform = (values, targetCount) => {
    if (!values.length || !targetCount) return []
    if (targetCount >= values.length) return values.slice()

    const result = []

    for (let index = 0; index < targetCount; index += 1) {
        const from = Math.floor((index * values.length) / targetCount)
        const to = Math.max(from + 1, Math.ceil(((index + 1) * values.length) / targetCount))
        let peak = 0

        for (let sourceIndex = from; sourceIndex < Math.min(values.length, to); sourceIndex += 1) {
            peak = Math.max(peak, values[sourceIndex])
        }

        result.push(peak)
    }

    return result
}

export default function SecureMessageAttachment({
    message,
    guestToken = '',
    onOpen,
}) {
    const [url, setUrl] = useState('')
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [downloading, setDownloading] = useState(false)
    const [retryNonce, setRetryNonce] = useState(0)
    const [videoRequested, setVideoRequested] = useState(false)
    const [notePlaying, setNotePlaying] = useState(false)
    const [noteLeft, setNoteLeft] = useState(0)
    const noteRef = useRef(null)

    const type = getChatAttachmentType(message)
    const name = getChatAttachmentName(message)
    const messageId = String(message?._id || message?.id || '')
    const fileSize = Number(message?.fileSize || message?.secureAttachment?.sizeBytes || 0)
    const meta = [formatBytes(fileSize), name].filter(Boolean).join(' · ')
    const isVideoNote = type === 'video' && message?.messageKind === 'video_note'
    const shouldPreload = type === 'image' || type === 'voice' || (isVideoNote && videoRequested)

    useEffect(() => {
        if (!hasSecureChatAttachment(message) || !shouldPreload || !messageId) {
            setUrl('')
            setLoading(false)
            setError('')
            return undefined
        }

        let cancelled = false
        let objectUrl = ''

        setLoading(true)
        setError('')
        setUrl('')

        fetchChatAttachment({ message, guestToken })
            .then((asset) => {
                if (cancelled || !asset?.blob) return

                objectUrl = URL.createObjectURL(asset.blob)
                setUrl(objectUrl)
            })
            .catch((loadError) => {
                if (cancelled) return
                setError(getAccessErrorText(loadError))
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })

        return () => {
            cancelled = true
            if (objectUrl) URL.revokeObjectURL(objectUrl)
        }
    }, [guestToken, messageId, shouldPreload, type, retryNonce])

    useEffect(() => {
        setVideoRequested(false)
        setNotePlaying(false)
        setNoteLeft(0)
    }, [messageId])

    const download = async () => {
        if (downloading || !messageId) return

        setDownloading(true)
        setError('')

        try {
            const asset = await fetchChatAttachment({
                message,
                guestToken,
                download: true,
                useCache: false,
            })

            downloadBlob({
                blob: asset.blob,
                filename: asset.filename || name,
            })
        } catch (downloadError) {
            setError(getAccessErrorText(downloadError))
        } finally {
            setDownloading(false)
        }
    }

    const locked = useMemo(() => (
        error ? `🔒 ${error}` : ''
    ), [error])

    if (!hasSecureChatAttachment(message)) return null

    if (locked) {
        return (
            <div className="attachment-AO attachment-AO--locked" role="status">
                <span className="attachment-AO__lock">🔒</span>
                <span>{error}</span>
                <button type="button" onClick={() => {
                    setError('')
                    if (isVideoNote) setVideoRequested(true)
                    setRetryNonce((value) => value + 1)
                }}>
                    Повторить
                </button>
            </div>
        )
    }

    if (type === 'image') {
        return (
            <button
                type="button"
                className="attachment-AO attachment-AO--image"
                onClick={() => onOpen?.(message)}
                disabled={loading || !url}
                title={name}
            >
                {url ? (
                    <img src={url} alt={name} loading="lazy" decoding="async" />
                ) : (
                    <span className="attachment-AO__skeleton" aria-label="Загружаем фото">
                        <i />
                        <b>Загружаем фото…</b>
                    </span>
                )}
            </button>
        )
    }

    if (isVideoNote) {
        const duration = Number(message?.mediaDurationSec || 0)

        const toggleNote = () => {
            const video = noteRef.current
            if (!url || !video) {
                setVideoRequested(true)
                return
            }
            if (video.paused) {
                if (video.ended) video.currentTime = 0
                video.play().catch(() => undefined)
            } else {
                video.pause()
            }
        }

        return (
            <>
            <button
                type="button"
                className={`attachment-AO attachment-AO--note ${notePlaying ? 'is-playing' : ''}`}
                onClick={toggleNote}
                disabled={loading}
                title={notePlaying ? 'Пауза' : 'Воспроизвести видеосообщение'}
            >
                {url ? (
                    <video
                        ref={noteRef}
                        src={url}
                        autoPlay
                        playsInline
                        preload="metadata"
                        onPlay={() => setNotePlaying(true)}
                        onPause={() => setNotePlaying(false)}
                        onEnded={() => {
                            setNotePlaying(false)
                            setNoteLeft(0)
                        }}
                        onTimeUpdate={(event) => {
                            const video = event.currentTarget
                            const total = Number.isFinite(video.duration) ? video.duration : duration
                            setNoteLeft(Math.max(0, total - video.currentTime))
                        }}
                    />
                ) : null}
                {!notePlaying ? (
                    <span className="attachment-AO__note-cover">
                        {loading ? <i className="attachment-AO__spinner" /> : <span className="attachment-AO__play">▶</span>}
                    </span>
                ) : null}
                {(duration > 0 || noteLeft > 0) && (
                    <span className="attachment-AO__note-time">
                        {formatDuration(notePlaying && noteLeft > 0 ? noteLeft : duration)}
                    </span>
                )}
            </button>
            {/* Что сказано в кружке — текстом (по кнопке «Расшифровать в текст»). */}
            <MediaTranscript message={message} guest={!!guestToken} maxWidth={240} />
            </>
        )
    }

    if (type === 'video') {
        return (
            <>
            <button
                type="button"
                className="attachment-AO attachment-AO--video attachment-AO--loadable"
                onClick={() => onOpen?.(message)}
                disabled={!onOpen}
                title="Запустить видео"
            >
                <span className="attachment-AO__play">▶</span>
                <span className="attachment-AO__loadable-text">
                    <b>Запустить видео</b>
                    <small>{meta || 'Видео'}</small>
                </span>
            </button>
            {/* Что сказано в видео — текстом (по кнопке «Расшифровать в текст»). */}
            <MediaTranscript message={message} guest={!!guestToken} maxWidth={360} />
            </>
        )
    }

    if (type === 'voice') {
        return (
            <VoiceAttachment
                message={message}
                url={url}
                loading={loading}
                name={name}
                fileSize={fileSize}
            />
        )
    }

    const openDocument = () => {
        if (onOpen) {
            onOpen(message)
            return
        }

        download()
    }

    // Что за файл: «Модель ArtCAM», «Чертёж DXF», «PDF»; у незнакомого — просто
    // расширение. Никакого «Фото» у файлов, которые фото не являются.
    const typeLabel = getFileTypeLabel({ name, mimeType: getChatAttachmentMimeType(message) })
    const extensionLabel = getFileExtensionLabel(name)
    const drawing = isDrawingFile(name)
    const docMeta = downloading
        ? 'Скачиваем…'
        : [typeLabel, formatBytes(fileSize)].filter(Boolean).join(' · ') || (onOpen ? 'Файл' : 'Нажмите, чтобы скачать')

    // Нажатие по файлу открывает его просмотр, отдельная кнопка справа — сразу скачивает.
    return (
        <div className={`attachment-AO-file ${drawing ? 'attachment-AO-file--drawing' : ''}`}>
            {drawing ? (
                <DrawingPreview
                    message={message}
                    name={name}
                    onOpen={openDocument}
                />
            ) : null}

            <div className="attachment-AO attachment-AO--doc attachment-AO--doc-row">
                <button
                    type="button"
                    className="attachment-AO__doc-open"
                    onClick={openDocument}
                    disabled={downloading}
                    title={onOpen ? (drawing ? 'Смотреть чертёж' : 'Открыть файл') : 'Скачать файл'}
                >
                    <span
                        className={`attachment-AO__doc-icon ${extensionLabel ? 'attachment-AO__doc-icon--ext' : ''}`}
                        data-length={extensionLabel.length}
                    >
                        {extensionLabel || '▤'}
                    </span>
                    <span className="attachment-AO__doc-main">
                        <b>{name}</b>
                        <small>{docMeta}</small>
                    </span>
                </button>
                <button
                    type="button"
                    className="attachment-AO__doc-download"
                    onClick={download}
                    disabled={downloading}
                    title="Скачать файл"
                    aria-label={`Скачать ${name}`}
                >
                    {downloading ? <i className="attachment-AO__spinner" /> : '↓'}
                </button>
            </div>
        </div>
    )
}

/**
 * Картинка чертежа DXF/SVG над карточкой файла. Сервер рисует её сам; грузим,
 * только когда сообщение видно на экране. Не получилось — картинки нет,
 * остаётся обычная карточка.
 */
function DrawingPreview({ message, name, onOpen }) {
    const nodeRef = useRef(null)
    const [visible, setVisible] = useState(false)
    const [url, setUrl] = useState('')
    const [state, setState] = useState('idle') // idle | loading | ready | failed
    const [reason, setReason] = useState('')
    const messageId = String(message?._id || message?.id || '')
    const local = !messageId || messageId.startsWith('local') || !!message?.__optimistic

    useEffect(() => {
        if (visible || local) return undefined
        const node = nodeRef.current
        if (!node || typeof IntersectionObserver === 'undefined') {
            setVisible(true)
            return undefined
        }
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) {
                setVisible(true)
                observer.disconnect()
            }
        }, { rootMargin: '300px' })
        observer.observe(node)
        return () => observer.disconnect()
    }, [local, visible])

    useEffect(() => {
        if (!visible || local) return undefined
        let cancelled = false
        let objectUrl = ''
        setState('loading')

        fetchChatDrawingPreview({ message, size: 'medium' })
            .then((asset) => {
                if (cancelled || !asset?.blob) return
                objectUrl = URL.createObjectURL(asset.blob)
                setUrl(objectUrl)
                setState('ready')
            })
            .catch(async (error) => {
                // Почему не нарисовалось — сервер пишет в ответе (он пришёл как файл).
                let text = ''
                try {
                    const data = error?.response?.data
                    const raw = data && typeof data.text === 'function' ? await data.text() : ''
                    const json = raw ? JSON.parse(raw) : null
                    text = String(json?.message || json?.error || '')
                } catch (_error) {
                    text = ''
                }
                const status = Number(error?.response?.status || 0)
                if (!text && (status === 404 || status === 501)) text = 'сервер нужно обновить и перезапустить'
                if (!cancelled) {
                    setReason(text)
                    setState('failed')
                }
            })

        return () => {
            cancelled = true
            if (objectUrl) URL.revokeObjectURL(objectUrl)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [visible, local, messageId])

    if (local) return null
    if (state === 'failed') {
        return reason ? (
            <span className="attachment-AO-file__drawing-error">Чертёж не нарисован: {reason}</span>
        ) : null
    }

    return (
        <button
            ref={nodeRef}
            type="button"
            className="attachment-AO-file__drawing"
            onClick={onOpen}
            title="Смотреть чертёж"
        >
            {url ? (
                <img src={url} alt={name} loading="lazy" decoding="async" draggable={false} />
            ) : (
                <span className="attachment-AO-file__drawing-wait">
                    <i className="attachment-AO__spinner" />
                    <b>Рисуем чертёж…</b>
                </span>
            )}
            <span className="attachment-AO-file__drawing-badge">Чертёж · нажмите, чтобы смотреть</span>
        </button>
    )
}

/**
 * Расшифровка видео и «кружка»: что сказано в записи — текстом под ней.
 * Сервер берёт из видео звук и распознаёт его так же, как голосовые.
 * Запускается кнопкой; готовый текст приходит сам и виден всем в чате.
 */
function MediaTranscript({ message, guest = false, maxWidth = 360 }) {
    const meta = getVoiceMeta(message)
    const text = String(meta?.transcriptText || '').trim()
    const status = String(meta?.transcriptStatus || '')
    const messageId = String(message?._id || message?.id || '')
    const sent = !!messageId && !messageId.startsWith('local:') && !message?.__sendStatus

    const [busy, setBusy] = useState(false)
    const [queued, setQueued] = useState(false)
    const [requestError, setRequestError] = useState('')
    const [expanded, setExpanded] = useState(false)
    const [copied, setCopied] = useState(false)

    // Сервер прислал новое состояние — своё «ждём» больше не нужно.
    useEffect(() => {
        setQueued(false)
        setRequestError('')
        setCopied(false)
    }, [status, meta?.updatedAt])

    useEffect(() => {
        if (!copied) return undefined

        const timer = window.setTimeout(() => setCopied(false), 1400)
        return () => window.clearTimeout(timer)
    }, [copied])

    const request = async () => {
        if (!messageId || busy) return

        setBusy(true)
        setQueued(true)
        setRequestError('')

        try {
            await requestVoiceTranscription(messageId)
        } catch (requestFailure) {
            setQueued(false)
            setRequestError(
                String(
                    requestFailure?.response?.data?.error ||
                    requestFailure?.response?.data?.message ||
                    requestFailure?.message ||
                    'Не удалось запустить расшифровку'
                )
            )
        } finally {
            setBusy(false)
        }
    }

    const copy = async () => {
        if (!text) return

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(text)
            } else {
                const textarea = document.createElement('textarea')
                textarea.value = text
                textarea.setAttribute('readonly', '')
                textarea.style.position = 'fixed'
                textarea.style.opacity = '0'
                document.body.appendChild(textarea)
                textarea.select()
                document.execCommand('copy')
                textarea.remove()
            }

            setCopied(true)
        } catch (_error) {
            setCopied(false)
        }
    }

    // Гость по ссылке расшифровку запустить не может; готовый текст он видит.
    if (!sent || (guest && !meta)) return null

    const effectiveStatus = queued || busy ? 'pending' : status
    const long = text.length > 220

    return (
        <div
            className="attachment-AO__transcript attachment-AO__transcript--media"
            data-chat-no-select="true"
            aria-live="polite"
            style={{ maxWidth, marginTop: 8 }}
        >
            {effectiveStatus === 'done' && text ? (
                <>
                    <div className="attachment-AO__transcript-content">
                        <div className={`attachment-AO__transcript-text ${long && !expanded ? 'is-collapsed' : ''}`}>
                            {text}
                        </div>
                        <button
                            type="button"
                            className={`attachment-AO__transcript-copy ${copied ? 'is-copied' : ''}`}
                            onClick={copy}
                            title={copied ? 'Скопировано' : 'Скопировать расшифровку'}
                            aria-label={copied ? 'Расшифровка скопирована' : 'Скопировать расшифровку'}
                        >
                            {copied ? <CheckOutlined /> : <ContentCopyOutlined />}
                        </button>
                    </div>
                    {long && (
                        <button
                            type="button"
                            className="attachment-AO__transcript-more"
                            onClick={() => setExpanded((value) => !value)}
                        >
                            {expanded ? 'Свернуть' : 'Показать полностью'}
                        </button>
                    )}
                </>
            ) : effectiveStatus === 'done' ? (
                <div className="attachment-AO__transcript-status">
                    <span>▤</span>
                    <span>Речь не распознана</span>
                </div>
            ) : effectiveStatus === 'pending' ? (
                <div className="attachment-AO__transcript-status">
                    <i className="attachment-AO__spinner" />
                    <span>Расшифровываем…</span>
                </div>
            ) : guest ? null : (
                <button
                    type="button"
                    className="attachment-AO__transcript-retry"
                    onClick={request}
                    disabled={busy}
                    title={requestError || 'Расшифровать речь из видео в текст'}
                >
                    <span>{effectiveStatus === 'error' || requestError ? '↻' : '▤'}</span>
                    <span>
                        {effectiveStatus === 'error' || requestError
                            ? `${requestError || 'Расшифровка недоступна'} · Повторить`
                            : 'Расшифровать в текст'}
                    </span>
                </button>
            )}
        </div>
    )
}

function VoiceAttachment({ message, url, loading, name, fileSize }) {
    const voiceMeta = getVoiceMeta(message)
    const fallbackDuration = Math.max(
        0,
        Number(voiceMeta?.durationSec || message?.mediaDurationSec || 0),
    )
    const transcriptText = String(voiceMeta?.transcriptText || '').trim()
    const transcriptStatus = String(voiceMeta?.transcriptStatus || '')
    const waveform = useMemo(() => normalizeWaveform(voiceMeta?.waveform), [voiceMeta?.waveform])

    const audioRef = useRef(null)
    const waveformRef = useRef(null)
    const transcriptMeasureRef = useRef(null)
    const [playing, setPlaying] = useState(false)
    const [buffering, setBuffering] = useState(false)
    const [currentTime, setCurrentTime] = useState(0)
    const [duration, setDuration] = useState(fallbackDuration)
    const [rate, setRate] = useState(1)
    const [waveWidth, setWaveWidth] = useState(0)
    const [transcriptExpanded, setTranscriptExpanded] = useState(false)
    const [transcriptOverflow, setTranscriptOverflow] = useState(false)
    const [transcriptBusy, setTranscriptBusy] = useState(false)
    const [transcriptQueued, setTranscriptQueued] = useState(false)
    const [transcriptRequestError, setTranscriptRequestError] = useState('')
    const [transcriptCopied, setTranscriptCopied] = useState(false)

    useEffect(() => {
        setDuration((current) => current > 0 ? current : fallbackDuration)
    }, [fallbackDuration])

    useEffect(() => {
        setTranscriptQueued(false)
        setTranscriptRequestError('')
        setTranscriptCopied(false)
    }, [transcriptStatus, voiceMeta?.updatedAt])

    useEffect(() => {
        if (!transcriptCopied) return undefined

        const timer = window.setTimeout(() => setTranscriptCopied(false), 1400)
        return () => window.clearTimeout(timer)
    }, [transcriptCopied])

    const measureTranscriptOverflow = useCallback(() => {
        const node = transcriptMeasureRef.current

        if (!node || !transcriptText) {
            setTranscriptOverflow(false)
            return
        }

        const styles = window.getComputedStyle(node)
        const lineHeight = Number.parseFloat(styles.lineHeight) || 17.4
        const nextOverflow = node.scrollHeight > lineHeight * 4 + 1

        setTranscriptOverflow((current) => current === nextOverflow ? current : nextOverflow)
        if (!nextOverflow) setTranscriptExpanded(false)
    }, [transcriptText])

    useLayoutEffect(() => {
        setTranscriptExpanded(false)
        measureTranscriptOverflow()
    }, [measureTranscriptOverflow])

    useEffect(() => {
        const node = transcriptMeasureRef.current
        if (!node || !transcriptText) return undefined

        const update = () => measureTranscriptOverflow()

        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(update)
            observer.observe(node)
            return () => observer.disconnect()
        }

        window.addEventListener('resize', update)
        return () => window.removeEventListener('resize', update)
    }, [measureTranscriptOverflow, transcriptText])

    useEffect(() => {
        const node = waveformRef.current
        if (!node || !waveform.length) {
            setWaveWidth(0)
            return undefined
        }

        const update = () => {
            const next = Math.max(0, Math.round(node.getBoundingClientRect().width))
            setWaveWidth((current) => current === next ? current : next)
        }

        update()

        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(update)
            observer.observe(node)
            return () => observer.disconnect()
        }

        window.addEventListener('resize', update)
        return () => window.removeEventListener('resize', update)
    }, [waveform.length])

    useEffect(() => {
        const audio = audioRef.current
        if (!audio || !url) return undefined

        const syncDuration = () => {
            const next = Number.isFinite(audio.duration) && audio.duration > 0
                ? audio.duration
                : fallbackDuration
            setDuration(Math.max(0, next || 0))
        }
        const syncTime = () => setCurrentTime(Math.max(0, Number(audio.currentTime || 0)))
        const onPlay = () => {
            setPlaying(true)
            setBuffering(false)
        }
        const onPause = () => {
            setPlaying(false)
            setBuffering(false)
            if (activeVoiceAudio === audio) activeVoiceAudio = null
        }
        const onWaiting = () => setBuffering(true)
        const onCanPlay = () => setBuffering(false)
        const onEnded = () => {
            audio.pause()
            audio.currentTime = 0
            setCurrentTime(0)
            setPlaying(false)
            setBuffering(false)
            if (activeVoiceAudio === audio) activeVoiceAudio = null
        }

        audio.addEventListener('loadedmetadata', syncDuration)
        audio.addEventListener('durationchange', syncDuration)
        audio.addEventListener('timeupdate', syncTime)
        audio.addEventListener('play', onPlay)
        audio.addEventListener('pause', onPause)
        audio.addEventListener('waiting', onWaiting)
        audio.addEventListener('canplay', onCanPlay)
        audio.addEventListener('ended', onEnded)

        syncDuration()
        syncTime()

        return () => {
            audio.removeEventListener('loadedmetadata', syncDuration)
            audio.removeEventListener('durationchange', syncDuration)
            audio.removeEventListener('timeupdate', syncTime)
            audio.removeEventListener('play', onPlay)
            audio.removeEventListener('pause', onPause)
            audio.removeEventListener('waiting', onWaiting)
            audio.removeEventListener('canplay', onCanPlay)
            audio.removeEventListener('ended', onEnded)
            audio.pause()
            if (activeVoiceAudio === audio) activeVoiceAudio = null
        }
    }, [fallbackDuration, url])

    useEffect(() => {
        if (audioRef.current) audioRef.current.playbackRate = rate
    }, [rate, url])

    const togglePlayback = useCallback(async () => {
        const audio = audioRef.current
        if (!audio || !url) return

        if (!audio.paused) {
            audio.pause()
            return
        }

        if (activeVoiceAudio && activeVoiceAudio !== audio) {
            activeVoiceAudio.pause()
        }

        activeVoiceAudio = audio
        audio.playbackRate = rate

        if (duration > 0 && audio.currentTime >= duration - 0.08) {
            audio.currentTime = 0
            setCurrentTime(0)
        }

        try {
            await audio.play()
        } catch (_error) {
            if (activeVoiceAudio === audio) activeVoiceAudio = null
            setPlaying(false)
            setBuffering(false)
        }
    }, [duration, rate, url])

    const seek = useCallback((event) => {
        const audio = audioRef.current
        const total = Math.max(duration, fallbackDuration)
        if (!audio || total <= 0) return

        const rect = event.currentTarget.getBoundingClientRect()
        if (!rect.width) return

        const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
        const next = Math.min(total, ratio * total)
        audio.currentTime = next
        setCurrentTime(next)
    }, [duration, fallbackDuration])

    const cycleRate = useCallback(() => {
        setRate((current) => {
            const index = VOICE_RATES.indexOf(current)
            return VOICE_RATES[(index + 1) % VOICE_RATES.length]
        })
    }, [])

    const retryTranscript = useCallback(async () => {
        const messageId = String(message?._id || message?.id || '')
        if (!messageId || transcriptBusy) return

        setTranscriptBusy(true)
        setTranscriptQueued(true)
        setTranscriptRequestError('')

        try {
            await requestVoiceTranscription(messageId)
        } catch (requestError) {
            setTranscriptQueued(false)
            setTranscriptRequestError(
                String(
                    requestError?.response?.data?.error ||
                    requestError?.response?.data?.message ||
                    requestError?.message ||
                    'Не удалось запустить расшифровку'
                )
            )
        } finally {
            setTranscriptBusy(false)
        }
    }, [message?._id, message?.id, transcriptBusy])

    const copyTranscript = useCallback(async () => {
        if (!transcriptText) return

        try {
            if (navigator?.clipboard?.writeText) {
                await navigator.clipboard.writeText(transcriptText)
            } else {
                const textarea = document.createElement('textarea')
                textarea.value = transcriptText
                textarea.setAttribute('readonly', '')
                textarea.style.position = 'fixed'
                textarea.style.opacity = '0'
                document.body.appendChild(textarea)
                textarea.select()
                document.execCommand('copy')
                textarea.remove()
            }

            setTranscriptCopied(true)
        } catch (_error) {
            setTranscriptCopied(false)
        }
    }, [transcriptText])

    const totalDuration = Math.max(duration, fallbackDuration)
    const progress = totalDuration > 0
        ? Math.max(0, Math.min(1, currentTime / totalDuration))
        : 0
    const targetBars = waveform.length
        ? Math.max(12, Math.min(waveform.length, Math.floor((waveWidth || 260) / 4)))
        : 0
    const bars = useMemo(
        () => compressWaveform(waveform, targetBars),
        [targetBars, waveform],
    )
    const playedBars = Math.round(progress * bars.length)
    const transcriptLong = transcriptOverflow
    const effectiveTranscriptStatus = transcriptQueued || transcriptBusy ? 'pending' : transcriptStatus

    return (
        <div className="attachment-AO attachment-AO--voice">
            <div className="attachment-AO__voice-title">
                <span>◉</span>
                <div>
                    <b>Голосовое сообщение</b>
                    {!!formatBytes(fileSize) && <small>{formatBytes(fileSize)}</small>}
                </div>
            </div>

            {url ? (
                <div className="attachment-AO__voice-player" data-chat-no-select="true">
                    <audio ref={audioRef} src={url} preload="metadata" />

                    <div className="attachment-AO__voice-controls">
                        <button
                            type="button"
                            className="attachment-AO__voice-play"
                            onClick={togglePlayback}
                            aria-label={playing ? 'Пауза' : 'Воспроизвести голосовое'}
                            title={playing ? 'Пауза' : 'Воспроизвести'}
                        >
                            {buffering
                                ? <i className="attachment-AO__spinner" />
                                : (playing ? 'Ⅱ' : '▶')}
                        </button>

                        <div className="attachment-AO__voice-track">
                            {waveform.length ? (
                                <button
                                    ref={waveformRef}
                                    type="button"
                                    className="attachment-AO__waveform"
                                    onClick={seek}
                                    aria-label="Перемотать голосовое"
                                    title="Нажмите на волну для перемотки"
                                >
                                    {bars.map((value, index) => (
                                        <span
                                            key={`${index}-${value}`}
                                            className={index < playedBars ? 'is-played' : ''}
                                            style={{ height: `${Math.max(3, 3 + (value / 100) * 25)}px` }}
                                        />
                                    ))}
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    className="attachment-AO__voice-progress"
                                    onClick={seek}
                                    aria-label="Перемотать голосовое"
                                >
                                    <span style={{ width: `${progress * 100}%` }} />
                                </button>
                            )}

                            <div className="attachment-AO__voice-meta">
                                <span>
                                    {playing || currentTime > 0
                                        ? `${formatDuration(currentTime)} / ${formatDuration(totalDuration)}`
                                        : formatDuration(totalDuration)}
                                </span>
                                <button
                                    type="button"
                                    className={`attachment-AO__voice-rate ${rate !== 1 ? 'is-active' : ''}`}
                                    onClick={cycleRate}
                                    title="Скорость воспроизведения"
                                >
                                    {String(rate).replace('.', ',')}x
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <span className="attachment-AO__audio-loading">
                    <i className="attachment-AO__spinner" />
                    {loading ? 'Загружаем аудио…' : name}
                </span>
            )}

            {!!voiceMeta && (
                <div className="attachment-AO__transcript" data-chat-no-select="true" aria-live="polite">
                    {effectiveTranscriptStatus === 'done' && transcriptText ? (
                        <>
                            <div className="attachment-AO__transcript-content">
                                <div
                                    className={`attachment-AO__transcript-text ${transcriptLong && !transcriptExpanded ? 'is-collapsed' : ''}`}
                                >
                                    {transcriptText}
                                </div>
                                <div
                                    ref={transcriptMeasureRef}
                                    className="attachment-AO__transcript-measure"
                                    aria-hidden="true"
                                >
                                    {transcriptText}
                                </div>
                                <button
                                    type="button"
                                    className={`attachment-AO__transcript-copy ${transcriptCopied ? 'is-copied' : ''}`}
                                    onClick={copyTranscript}
                                    title={transcriptCopied ? 'Скопировано' : 'Скопировать расшифровку'}
                                    aria-label={transcriptCopied ? 'Расшифровка скопирована' : 'Скопировать расшифровку'}
                                >
                                    {transcriptCopied ? <CheckOutlined /> : <ContentCopyOutlined />}
                                </button>
                            </div>
                            {transcriptLong && (
                                <button
                                    type="button"
                                    className="attachment-AO__transcript-more"
                                    onClick={() => setTranscriptExpanded((value) => !value)}
                                >
                                    {transcriptExpanded ? 'Свернуть' : 'Показать полностью'}
                                </button>
                            )}
                        </>
                    ) : effectiveTranscriptStatus === 'done' ? (
                        <div className="attachment-AO__transcript-status">
                            <span>▤</span>
                            <span>Речь не распознана</span>
                        </div>
                    ) : effectiveTranscriptStatus === 'pending' ? (
                        <div className="attachment-AO__transcript-status">
                            <i className="attachment-AO__spinner" />
                            <span>Расшифровываем…</span>
                        </div>
                    ) : effectiveTranscriptStatus === 'error' || transcriptRequestError ? (
                        <button
                            type="button"
                            className="attachment-AO__transcript-retry"
                            onClick={retryTranscript}
                            disabled={transcriptBusy}
                            title={transcriptRequestError || 'Запустить расшифровку ещё раз'}
                        >
                            <span>↻</span>
                            <span>{transcriptRequestError || 'Расшифровка недоступна'} · Повторить</span>
                        </button>
                    ) : null}
                </div>
            )}
        </div>
    )
}
